import { useTrainComposition } from "features/CenterContent/VehicleDetails/TrainDetails/TrainComposition/useTrainComposition";
import { CommonTrainInfoFormProps } from "features/CenterContent/VehicleDetails/TrainDetails/TrainCondition/OperationalTrainInfo/TrainInfoModal/TrainInfoForm";
import { CommonSubTypeProps } from "features/CenterContent/VehicleDetails/TrainDetails/TrainCondition/OperationalTrainInfo/TrainInfoModal/TrainInfoFormModal";
import { AffectedStopsFields } from "features/CenterContent/VehicleDetails/TrainDetails/TrainCondition/OperationalTrainInfo/TrainInfoModal/subTypeInputs/AffectedStopsFields";
import {
  calcTrainCompMetaData,
  getDefaultCapacity,
  getNumPassengerCarsFromLineId,
} from "features/CenterContent/VehicleDetails/TrainDetails/TrainCondition/OperationalTrainInfo/TrainInfoModal/subTypeInputs/utils/changedCapacity";
import { TrainContext } from "features/CenterContent/VehicleDetails/TrainDetails/TrainCondition/OperationalTrainInfo/TrainInfoModal/subTypeInputs/utils/formContextWrappers";
import { EitherTrainFormSchema } from "features/CenterContent/VehicleDetails/TrainDetails/TrainCondition/OperationalTrainInfo/TrainInfoModal/types/formSchema";
import { useTrainInfoContext } from "features/CenterContent/VehicleDetails/TrainDetails/TrainCondition/OperationalTrainInfo/TrainInfoModal/useTrainInfoContext";
import { FC, useEffect } from "react";
import { Controller, useFormContext } from "react-hook-form";
import { useTranslation } from "react-i18next";
import {
  Box,
  Field,
  HStack,
  NumericStepper,
  Text,
  VStack,
} from "shared/components/Spor";

const MIN_CARS_OLD = 2;
const MIN_CARS_NEW = 1;
const MAX_CARS = 15;

/**
 * Could be combined with TrainCapacityIncreased.tsx to make TrainCapacityBase.tsx
 * However, this component is rarely touched so its likely not worth the time
 */
export const TrainCapacityReduced: FC<
  CommonTrainInfoFormProps & CommonSubTypeProps & TrainContext
> = (props) => {
  const { mode, trainId, trainFormPrefix } = props;
  const { t } = useTranslation();

  const { getValues, setValue, control } =
    useFormContext<EitherTrainFormSchema>();

  const oldCapacityUnitPath = `${trainFormPrefix}.oldCapacityUnit` as const;
  const newCapacityUnitPath = `${trainFormPrefix}.newCapacityUnit` as const;
  const oldCapacityPath = `${trainFormPrefix}.oldCapacity` as const;
  const newCapacityPath = `${trainFormPrefix}.newCapacity` as const;

  // Set capacity unit once on first load
  useEffect(() => {
    setValue(oldCapacityUnitPath, "CARS");
    setValue(newCapacityUnitPath, "CARS");
  }, []);

  const oldCapacity = getValues(oldCapacityPath) ?? MIN_CARS_OLD;
  const newCapacity = getValues(newCapacityPath) ?? MIN_CARS_NEW;

  const { status: compositionStatus, data: compositionData } =
    useTrainComposition(trainId);

  const { useTrainRouteBatch } = useTrainInfoContext();
  const { data: trainRoute } = useTrainRouteBatch(trainId);

  // Autofill based on train composition
  useEffect(() => {
    if (
      mode === "create" ||
      (!getValues(oldCapacityPath) && !getValues(newCapacityPath))
    ) {
      if (
        compositionStatus === "success" &&
        compositionData.trainComposition.length > 0
      ) {
        const { numPassengerCars, type } = calcTrainCompMetaData(
          compositionData.trainComposition,
        );
        const capacity = getDefaultCapacity(numPassengerCars, type, "reduced");
        setValue(oldCapacityPath, capacity.old);
        setValue(newCapacityPath, capacity.new);
      } else {
        const numPassengerCars = trainRoute?.lineId
          ? getNumPassengerCarsFromLineId(trainRoute.lineId)
          : 5;
        const capacity = getDefaultCapacity(
          numPassengerCars,
          "MULTIPLE_UNIT",
          "reduced",
        );
        if (!getValues(oldCapacityPath)) {
          setValue(oldCapacityPath, capacity.old);
        }

        if (!getValues(newCapacityPath)) {
          setValue(newCapacityPath, capacity.new);
        }
      }
    }
  }, [compositionStatus]);

  return (
    <VStack width="100%" alignItems="flex-start" gap="18px">
      <AffectedStopsFields {...props} />
      <VStack paddingTop="1rem">
        <Box>
          <Field marginBottom="0">{t("Gammel kapasitet")}</Field>
          <HStack gap="1rem" alignContent="center" display="flex">
            <Controller
              control={control}
              name={oldCapacityPath}
              render={({ field }) => (
                <NumericStepper
                  name={oldCapacityPath}
                  minValue={MIN_CARS_OLD}
                  maxValue={MAX_CARS}
                  withInput={false}
                  value={field.value}
                  onValueChange={(value) => {
                    field.onChange(value);

                    // Make sure 'newCapacity' is always lower than 'oldCapacity'
                    if (value - 1 < newCapacity) {
                      setValue(
                        newCapacityPath,
                        Math.max(value - 1, MIN_CARS_NEW),
                      );
                    }
                  }}
                />
              )}
            />
            <Text>{t("Vogner")}</Text>
          </HStack>
        </Box>
        <Box>
          <Field marginBottom="0">{t("Ny kapasitet")}</Field>
          <HStack gap="1rem">
            <Controller
              control={control}
              name={newCapacityPath}
              render={({ field }) => (
                <NumericStepper
                  name={newCapacityPath}
                  minValue={MIN_CARS_NEW}
                  maxValue={oldCapacity - 1}
                  withInput={false}
                  value={field.value}
                  onValueChange={(value) => field.onChange(value)}
                />
              )}
            />
            <Text>{t("Vogner")}</Text>
          </HStack>
        </Box>
      </VStack>
    </VStack>
  );
};
